export const expectedTeamSizeOptions = [
  { key: '1-10', label: '1–10' },
  { key: '11-25', label: '11–25' },
  { key: '26-50', label: '26–50' },
  { key: '51-200', label: '51–200' },
  { key: '201-500', label: '201–500' },
  { key: '501-1000', label: '501–1000' },
  { key: '1000+', label: '1000+' },
] as const;

export const industryOptions = [
  { key: 'construction', labelKey: 'team_info_step.industry.construction' },
  { key: 'education', labelKey: 'team_info_step.industry.education' },
  { key: 'finance_accounting', labelKey: 'team_info_step.industry.finance_accounting' },
  { key: 'healthcare', labelKey: 'team_info_step.industry.healthcare' },
  { key: 'hospitality', labelKey: 'team_info_step.industry.hospitality' },
  { key: 'marketing_agencies', labelKey: 'team_info_step.industry.marketing_agencies' },
  { key: 'retail_ecommerce', labelKey: 'team_info_step.industry.retail_ecommerce' },
  { key: 'software_it', labelKey: 'team_info_step.industry.software_it' },
  { key: 'other', labelKey: 'team_info_step.industry.other' },
] as const;

export const teamWorkStyleOptions = [
  { key: 'remote', labelKey: 'team_info_step.team_work_style.remote', icon: 'laptop' as const },
  { key: 'hybrid', labelKey: 'team_info_step.team_work_style.hybrid', icon: 'briefcase' as const },
  { key: 'in_office', labelKey: 'team_info_step.team_work_style.in_office', icon: 'building' as const },
] as const;

export const jobLevelOptions = [
  { key: 'owner_founder', labelKey: 'team_info_step.job_level.owner_founder' },
  { key: 'c_suite', labelKey: 'team_info_step.job_level.c_suite' },
  { key: 'vp_director', labelKey: 'team_info_step.job_level.vp_director' },
  { key: 'manager_team_lead', labelKey: 'team_info_step.job_level.manager_team_lead' },
  { key: 'individual_contributor', labelKey: 'team_info_step.job_level.individual_contributor' },
] as const;

export const departmentOptions = [
  { key: 'customer_experience', labelKey: 'team_info_step.department.customer_experience' },
  { key: 'engineering_product', labelKey: 'team_info_step.department.engineering_product' },
  { key: 'finance', labelKey: 'team_info_step.department.finance' },
  { key: 'general_management', labelKey: 'team_info_step.department.general_management' },
  { key: 'hr', labelKey: 'team_info_step.department.hr' },
  { key: 'it_security', labelKey: 'team_info_step.department.it_security' },
  { key: 'operations', labelKey: 'team_info_step.department.operations' },
  { key: 'revenue', labelKey: 'team_info_step.department.revenue' },
] as const;

export const usedTimeTrackingOptions = [
  { key: 'yes', labelKey: 'team_info_step.used_time_tracking.yes', icon: 'check' as const },
  { key: 'no', labelKey: 'team_info_step.used_time_tracking.no', icon: 'close' as const },
] as const;

export type TeamInfoFormValues = {
  expectedteamsize: string;
  industry: string;
  teamworkstyle: string;
  userrole: string;
  department: string;
  usedtimetrackingbefore: string;
};

type TranslateFn = (key: string) => string;

function normalizeOptionValue(
  value: string | undefined,
  options: ReadonlyArray<{ key: string; labelKey: string }>,
  t: TranslateFn,
): string {
  if (!value) return '';

  const byKey = options.find(option => option.key === value);
  if (byKey) return t(byKey.labelKey);

  if (options.some(option => t(option.labelKey) === value)) return value;

  return value;
}

/**
 * Drops values that match no current bucket (e.g. answers from the old 1–5 / 6–20 / 200+ ranges),
 * so the user re-picks instead of seeing a value the select can't show.
 */
export function normalizeExpectedTeamSize(value: string | undefined): string {
  if (!value) return '';

  const byKey = expectedTeamSizeOptions.find(option => option.key === value);
  if (byKey) return byKey.label;

  return expectedTeamSizeOptions.some(option => option.label === value) ? value : '';
}

/**
 * Like normalizeOptionValue, but drops values that match no option. Used for fields whose
 * option list replaced an older one, so stale answers are cleared instead of shown.
 */
function normalizeStrictOptionValue(
  value: string | undefined,
  options: ReadonlyArray<{ key: string; labelKey: string }>,
  t: TranslateFn,
): string {
  if (!value) return '';

  const byKey = options.find(option => option.key === value);
  if (byKey) return t(byKey.labelKey);

  return options.some(option => t(option.labelKey) === value) ? value : '';
}

export function normalizeTeamInfoFormValues(
  custom: Record<string, string | undefined> | undefined,
  t: TranslateFn,
): TeamInfoFormValues {
  return {
    expectedteamsize: normalizeExpectedTeamSize(custom?.expectedteamsize),
    industry: normalizeOptionValue(custom?.industry, industryOptions, t),
    teamworkstyle: normalizeOptionValue(custom?.teamworkstyle, teamWorkStyleOptions, t),
    userrole: normalizeStrictOptionValue(custom?.userrole, jobLevelOptions, t),
    department: normalizeStrictOptionValue(custom?.department, departmentOptions, t),
    usedtimetrackingbefore: normalizeOptionValue(custom?.usedtimetrackingbefore, usedTimeTrackingOptions, t),
  };
}

export const emptyTeamInfoFormValues: TeamInfoFormValues = {
  expectedteamsize: '',
  industry: '',
  teamworkstyle: '',
  userrole: '',
  department: '',
  usedtimetrackingbefore: '',
};

export function isTeamInfoComplete(values: TeamInfoFormValues): boolean {
  return Object.values(values).every(value => Boolean(value));
}

export function buildTeamInfoHubSpotProperties(values: TeamInfoFormValues): Record<string, string> {
  return {
    expectedteamsize: values.expectedteamsize,
    industry_onb: values.industry,
    teamworkstyle: values.teamworkstyle,
    userrole: values.userrole,
    department_onb: values.department,
    usedtimetrackingbefore: values.usedtimetrackingbefore,
  };
}
